<script setup lang="ts">
import { ActionButton, StatusTag } from '@taler/ui'
import { reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import RoleSelect from '@/features/membership/components/RoleSelect.vue'
import { isLastOwner } from '../internal/roles.ts'
import type { LedgerRole, Member } from '../internal/types.ts'

const props = defineProps<{
  members: readonly Member[]
  isAdding: boolean
  addError: Error | null
  changingRoleSubject: string | undefined
  changeRoleError: Error | null
  removingSubject: string | undefined
  removeError: Error | null
}>()

const emit = defineEmits<{
  add: [input: { subject: string; role: LedgerRole }]
  changeRole: [input: { subject: string; role: LedgerRole }]
  remove: [subject: string]
}>()

const { t } = useI18n()

const form = reactive<{ subject: string; role: LedgerRole }>({ subject: '', role: 'OBSERVER' })
const confirmingSubject = ref<string | undefined>(undefined)

function submit(): void {
  if (form.subject.trim() === '') return
  emit('add', { subject: form.subject.trim(), role: form.role })
}

watch(
  () => props.isAdding,
  (adding, wasAdding) => {
    if (wasAdding && !adding && props.addError === null) form.subject = ''
  },
)

function changeRole(member: Member, role: LedgerRole): void {
  if (role === member.role) return
  emit('changeRole', { subject: member.subject, role })
}

function confirmRemove(subject: string): void {
  emit('remove', subject)
}

watch(
  () => props.removingSubject,
  (removing, wasRemoving) => {
    if (wasRemoving !== undefined && removing === undefined && props.removeError === null)
      confirmingSubject.value = undefined
  },
)
</script>

<template>
  <section class="flex flex-col gap-[var(--space-block)]">
    <h2 class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
      {{ t('membership.membersTitle') }}
    </h2>

    <p v-if="props.members.length === 0" class="text-[color:var(--color-text-muted)]">
      {{ t('membership.membersEmpty') }}
    </p>

    <table v-else class="w-full border-collapse bg-[color:var(--color-surface)]">
      <thead>
        <tr class="table-head">
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('membership.subject') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            {{ t('membership.role') }}
          </th>
          <th class="px-[var(--space-cell)] py-[var(--space-inline)]" scope="col">
            <span class="sr-only">{{ t('membership.remove') }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="member in props.members"
          :key="member.subject"
          class="border-b border-[color:var(--color-border)]"
        >
          <td
            class="px-[var(--space-cell)] py-[var(--space-cell)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
          >
            {{ member.name ?? member.subject }}
          </td>
          <td class="px-[var(--space-cell)] py-[var(--space-cell)]">
            <template v-if="isLastOwner(props.members, member.subject)">
              <span
                class="whitespace-nowrap rounded-full border border-[color:var(--color-border)] px-[var(--space-inline)] py-[var(--space-hair)] text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
              >
                {{ t(`membership.roleName.${member.role}`) }}
              </span>
              <p class="mt-1 text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                {{ t('membership.lastOwnerNote') }}
              </p>
            </template>
            <div v-else class="w-56">
              <RoleSelect
                :id="`member-role-${member.subject}`"
                :model-value="member.role"
                :disabled="props.changingRoleSubject === member.subject"
                :aria-label="t('membership.roleFor', { subject: member.name ?? member.subject })"
                @update:model-value="changeRole(member, $event)"
              />
            </div>
          </td>
          <td class="px-[var(--space-cell)] py-[var(--space-cell)] text-right">
            <template v-if="!isLastOwner(props.members, member.subject)">
              <div
                v-if="confirmingSubject === member.subject"
                class="flex items-center justify-end gap-[var(--space-inline)]"
              >
                <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
                  {{ t('membership.removeConfirmBody', { subject: member.name ?? member.subject }) }}
                </span>
                <button
                  type="button"
                  :disabled="props.removingSubject === member.subject"
                  class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] bg-[color:var(--color-danger)] px-3 py-1 text-[length:var(--text-step-2)] text-[color:var(--color-text-inverse)] disabled:opacity-60"
                  @click="confirmRemove(member.subject)"
                >
                  {{ t('membership.removeConfirmAction') }}
                </button>
                <button
                  type="button"
                  class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-1 text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
                  @click="confirmingSubject = undefined"
                >
                  {{ t('membership.removeCancel') }}
                </button>
              </div>
              <button
                v-else
                type="button"
                class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-1 text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
                @click="confirmingSubject = member.subject"
              >
                {{ t('membership.remove') }}
              </button>
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <StatusTag v-if="props.changeRoleError" tone="danger" role="alert">
      {{ t('membership.changeRoleError') }}
    </StatusTag>
    <StatusTag v-if="props.removeError" tone="danger" role="alert">
      {{ t('membership.removeError') }}
    </StatusTag>

    <form
      class="flex flex-col gap-[var(--space-group)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-[var(--space-block)] bg-[color:var(--color-surface)]"
      @submit.prevent="submit"
    >
      <div>
        <h3 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ t('membership.addMemberTitle') }}
        </h3>
        <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
          {{ t('membership.addMemberBody') }}
        </p>
      </div>

      <div class="flex items-end gap-[var(--space-group)]">
        <div class="flex flex-1 flex-col gap-[var(--space-tight)]">
          <label
            for="member-subject"
            class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ t('membership.subject') }}
          </label>
          <input
            id="member-subject"
            v-model="form.subject"
            type="email"
            required
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[length:var(--text-step-3)] text-[color:var(--color-text)]"
          />
        </div>
        <div class="w-56">
          <label
            for="member-role"
            class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
          >
            {{ t('membership.role') }}
          </label>
          <RoleSelect id="member-role" v-model="form.role" />
        </div>
        <ActionButton
          type="submit"
          :disabled="props.isAdding"
          class="shrink-0 text-[length:var(--text-step-3)]"
        >
          {{ t('membership.addMemberAction') }}
        </ActionButton>
      </div>

      <StatusTag v-if="props.addError" tone="danger" role="alert">
        {{ t('membership.addMemberError') }}
      </StatusTag>
    </form>
  </section>
</template>
